import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, Plus, Store } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function Businesses() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/businesses", { params: q ? { q } : {} }).then((r) => setItems(r.data.items));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div>
      <section className="bg-[#F7F4EB] border-b border-[#E2DED0]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Businesses</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#1E3A2F] mt-1">Community-recommended providers</h1>
          <p className="text-[#5C5C5C] mt-2 max-w-2xl">Every recommendation the community writes lives on a public page. Claim yours, or find a trusted local business.</p>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link to="/businesses/new" data-testid="biz-create-btn" className="inline-flex items-center gap-2 rounded-full abm-btn-primary px-5 h-11 font-medium">
              <Plus className="w-4 h-4" /> Add your business
            </Link>
            <div className="flex items-center gap-2 bg-white rounded-full border border-[#E2DED0] px-3 flex-1 max-w-md">
              <Search className="w-4 h-4 text-[#5C5C5C]" />
              <Input data-testid="biz-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search businesses…" className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0" />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
            <Store className="w-8 h-8 text-[#C05A3D] mx-auto" />
            <div className="mt-3 font-display text-xl text-[#1E3A2F]">No businesses yet</div>
            <p className="text-sm text-[#5C5C5C] mt-1">Be the first — build a permanent page for your work.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((b) => <BusinessCard key={b.id} b={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function BusinessCard({ b }) {
  return (
    <Link to={`/business/${b.slug}`} data-testid={`biz-card-${b.slug}`} className="abm-card p-5 flex flex-col fade-in">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#F7F4EB] grid place-items-center overflow-hidden">
          {b.logo_path ? <img src={imageUrl(b.logo_path)} alt="" className="w-full h-full object-cover" /> : <Store className="w-5 h-5 text-[#C05A3D]" />}
        </div>
        <div>
          <div className="font-display text-lg text-[#1A1A1A] leading-tight">{b.name}</div>
          <div className="text-xs text-[#5C5C5C]">{b.category} · {b.location}</div>
        </div>
      </div>
      {b.tagline && <p className="mt-3 text-sm text-[#5C5C5C] line-clamp-2">{b.tagline}</p>}
      <div className="mt-4 flex items-center justify-between pt-4 border-t border-[#E2DED0]">
        {b.verified ? <VerifiedBadge tier={b.verification_tier} size="sm" /> : <span className="text-xs text-[#5C5C5C]">Community listed</span>}
        <span className="text-xs font-medium text-[#C05A3D]">{b.recommendation_count} recommendation{b.recommendation_count === 1 ? "" : "s"}</span>
      </div>
    </Link>
  );
}
